import React, { useEffect, useState } from 'react';
import { Star, GripVertical, X } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Block } from './ui/Block';
import { useWishlist } from '../hooks/useWishlist';
import type { WishlistEntry } from '../types/social';

interface Props {
  userId: string;
}

export function MyPrioritiesSection({ userId }: Props) {
  const { wishlist, loading, remove, reorder } = useWishlist(userId);
  const [items, setItems] = useState<WishlistEntry[]>([]);

  useEffect(() => {
    setItems(wishlist);
  }, [wishlist]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.sticker_code === active.id);
    const newIndex = items.findIndex((i) => i.sticker_code === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(items, oldIndex, newIndex).map((it, idx) => ({
      ...it,
      priority: idx + 1,
    }));
    setItems(next);
    void reorder(next.map((it) => it.sticker_code));
  };

  return (
    <Block
      title="Minhas prioridades"
      count={items.length}
      countTone="gold"
      defaultOpen
      description="Até 5 figurinhas que você mais quer. Amigos com elas repetidas vão te ver primeiro."
    >
      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-12 bg-ink-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-ink-300 bg-paper py-6 px-4 text-center">
          <Star size={28} className="mx-auto text-ink-300 mb-2" />
          <p className="text-small text-ink-500">
            Marca até 5 figurinhas como prioridade tocando na estrela do card no álbum.
          </p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={items.map((it) => it.sticker_code)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="space-y-2">
              {items.map((it, idx) => (
                <SortablePriorityItem
                  key={it.sticker_code}
                  entry={{ ...it, priority: idx + 1 }}
                  onRemove={() => remove(it.sticker_code)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </Block>
  );
}

interface ItemProps {
  entry: WishlistEntry;
  onRemove: () => void;
}

function SortablePriorityItem({ entry, onRemove }: ItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry.sticker_code,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 bg-white border border-ink-300 rounded-lg pl-2 pr-3 py-2.5 shadow-xs"
    >
      <button
        type="button"
        aria-label="Reordenar"
        className="p-1.5 text-ink-500 hover:text-ink-900 cursor-grab active:cursor-grabbing rounded-md btn-focus touch-none"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={18} />
      </button>
      <span className="w-7 h-7 rounded-full bg-green-700 text-white font-display font-bold flex items-center justify-center text-[12px] shrink-0">
        {entry.priority}
      </span>
      <span className="font-mono font-semibold text-ink-900 flex-1">{entry.sticker_code}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remover prioridade"
        className="p-1.5 text-ink-500 hover:text-danger hover:bg-ruby-100 rounded-md btn-focus"
      >
        <X size={16} />
      </button>
    </li>
  );
}
